<template>
  <div class="notif-channel-selector">
    <div class="section-hint-block">
      <p class="section-hint">選擇要發送的通知管道，並為每個管道指定範本。</p>
      <p v-if="showDefaultTemplatePreview" class="section-hint section-hint--emphasis">
        若管道已啟用但<strong>未指定範本</strong>，系統會自動套用下方預設<strong>純文字</strong>（支援
        <code v-pre>{{memberName}}</code> 等變數，發送時會替換為實際內容）。
        <template v-if="templatePreviewPreset === 'points_expiry'">
          點數到期提醒另支援
          <code v-pre>{{totalPoints}}</code>、<code v-pre>{{expiryDate}}</code>、<code v-pre>{{daysUntilExpiry}}</code>、<code v-pre>{{reminderDays}}</code>。
        </template>
        <template v-if="templatePreviewPreset === 'tier_downgrade'">
          降等前提醒另支援
          <code v-pre>{{tierName}}</code>、<code v-pre>{{settlementDate}}</code>、<code v-pre>{{amountToKeep}}</code>、<code v-pre>{{daysUntilSettlement}}</code>。
        </template>
      </p>
      <ul class="send-logic-list">
        <li><i class="ri-line-fill" style="color:#06c755"></i> <strong>LINE</strong>：優先發送，若會員已綁定 LINE 則透過 LINE 通知</li>
        <li><i class="ri-message-2-line" style="color:#f59e0b"></i> <strong>SMS</strong>：備用管道，僅在會員<strong>沒有綁定 LINE</strong> 時發送簡訊</li>
        <li><i class="ri-mail-line" style="color:#3b82f6"></i> <strong>Email</strong>：獨立發送，只要會員有 Email 且勾選此管道，<strong>一律發送</strong></li>
      </ul>
    </div>

    <div class="notification-channels">
      <!-- LINE -->
      <div class="notification-channel-row" :class="{ 'is-expanded': channels.includes('line') && !lineFixed }">
        <div class="channel-row-header">
          <i class="ri-line-fill channel-icon line"></i>
          <span class="channel-name">LINE</span>
          <span class="channel-logic-hint">優先發送</span>
          <label class="ch-toggle-switch">
            <input
              type="checkbox"
              :checked="channels.includes('line')"
              @change="toggle('line', $event.target.checked)"
            />
            <span class="ch-toggle-slider"></span>
          </label>
          <span class="ch-toggle-status" :class="channels.includes('line') ? 'on' : 'off'">
            {{ channels.includes('line') ? '已啟用' : '已停用' }}
          </span>
          <!-- 固定格式模式（BatchIssue 使用） -->
          <span v-if="lineFixed && channels.includes('line')" class="channel-fixed-hint">
            <i class="ri-information-line"></i>
            使用固定票券通知格式
          </span>
        </div>

        <!-- 一般模式：使用範本選擇元件 -->
        <div v-if="!lineFixed && channels.includes('line')" class="line-picker-wrapper">
          <LineMsgTemplatePicker
            audience-filter="personal"
            :model-value="lineSelectedTemplate"
            :selected-id="templateIds.line || null"
            @update:model-value="onLineTemplateSelect"
          />
          <div class="picker-goto-link">
            <router-link :to="{ name: 'LineMsgTemplateCreate' }" target="_blank">
              <i class="ri-add-line"></i> 新增 LINE 範本
            </router-link>
          </div>
          <div
            v-if="showDefaultTemplatePreview && !templateIds.line"
            class="default-template-preview"
          >
            <p class="default-preview-title">
              <i class="ri-file-text-line"></i> 未選範本時的預設文字（LINE）
            </p>
            <pre class="default-preview-body">{{ defaultLineBody }}</pre>
          </div>
        </div>
      </div>

      <!-- SMS -->
      <div class="notification-channel-row" :class="{ 'is-expanded': channels.includes('sms') }">
        <div class="channel-row-header" style="flex:1;min-width:0;">
          <i class="ri-message-2-line channel-icon sms"></i>
          <span class="channel-name">SMS</span>
          <span class="channel-logic-hint">無 LINE 時備用</span>
          <label class="ch-toggle-switch">
            <input
              type="checkbox"
              :checked="channels.includes('sms')"
              @change="toggle('sms', $event.target.checked)"
            />
            <span class="ch-toggle-slider"></span>
          </label>
          <span class="ch-toggle-status" :class="channels.includes('sms') ? 'on' : 'off'">
            {{ channels.includes('sms') ? '已啟用' : '已停用' }}
          </span>
        </div>
        <div v-if="channels.includes('sms')" class="template-row">
          <select
            class="form-control template-select"
            :value="templateIds.sms || ''"
            @change="setTemplate('sms', $event.target.value)"
          >
            <option value="">— 選擇 SMS 範本 —</option>
            <option v-for="t in smsTemplates" :key="t.id" :value="t.id">{{ t.name }}</option>
          </select>
          <span v-if="smsTemplates.length === 0" class="no-template-hint">
            尚無可用 SMS 範本
            <router-link :to="{ name: 'NotificationTemplates' }" target="_blank">前往新增</router-link>
          </span>
          <div
            v-if="showDefaultTemplatePreview && !templateIds.sms"
            class="default-template-preview default-template-preview--full"
          >
            <p class="default-preview-title">
              <i class="ri-file-text-line"></i> 未選範本時的預設文字（SMS）
            </p>
            <pre class="default-preview-body">{{ defaultSmsBody }}</pre>
          </div>
        </div>
      </div>

      <!-- Email -->
      <div class="notification-channel-row" :class="{ 'is-expanded': channels.includes('email') }">
        <div class="channel-row-header" style="flex:1;min-width:0;">
          <i class="ri-mail-line channel-icon email"></i>
          <span class="channel-name">Email</span>
          <span class="channel-logic-hint channel-logic-hint--always">有 Email 一律發送</span>
          <label class="ch-toggle-switch">
            <input
              type="checkbox"
              :checked="channels.includes('email')"
              @change="toggle('email', $event.target.checked)"
            />
            <span class="ch-toggle-slider"></span>
          </label>
          <span class="ch-toggle-status" :class="channels.includes('email') ? 'on' : 'off'">
            {{ channels.includes('email') ? '已啟用' : '已停用' }}
          </span>
        </div>
        <div v-if="channels.includes('email')" class="template-row">
          <select
            class="form-control template-select"
            :value="templateIds.email || ''"
            @change="setTemplate('email', $event.target.value)"
          >
            <option value="">— 選擇 Email 範本 —</option>
            <option v-for="t in emailTemplates" :key="t.id" :value="t.id">{{ t.name }}</option>
          </select>
          <span v-if="emailTemplates.length === 0" class="no-template-hint">
            尚無可用 Email 範本
            <router-link :to="{ name: 'NotificationTemplates' }" target="_blank">前往新增</router-link>
          </span>
          <div
            v-if="showDefaultTemplatePreview && !templateIds.email"
            class="default-template-preview default-template-preview--full"
          >
            <p class="default-preview-title">
              <i class="ri-file-text-line"></i> 未選範本時的預設文字（Email）
            </p>
            <p class="default-preview-subject">
              <span class="default-preview-label">主旨</span>
              <span>{{ defaultEmailSubject }}</span>
            </p>
            <pre class="default-preview-body">{{ defaultEmailBody }}</pre>
          </div>
        </div>
      </div>
    </div>

    <div class="manage-templates-link">
      <router-link :to="{ name: 'LineMsgTemplates' }" target="_blank">
        <i class="ri-external-link-line"></i> 管理 LINE 訊息範本
      </router-link>
      <span class="link-sep">｜</span>
      <router-link :to="{ name: 'NotificationTemplates' }" target="_blank">
        <i class="ri-external-link-line"></i> 管理 Email/SMS 通知範本
      </router-link>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { notificationTemplateService } from '@/api/modules/notificationTemplates.js';
import LineMsgTemplatePicker from '@/components/LineMsgTemplatePicker.vue';
import {
  DEFAULT_AUTO_SEND_LINE_BODY,
  DEFAULT_AUTO_SEND_SMS_BODY,
  DEFAULT_AUTO_SEND_EMAIL_SUBJECT,
  DEFAULT_AUTO_SEND_EMAIL_BODY,
} from '@/constants/defaultAutoSendNotificationTemplates.js';
import {
  DEFAULT_POINTS_EXPIRY_LINE_BODY,
  DEFAULT_POINTS_EXPIRY_SMS_BODY,
  DEFAULT_POINTS_EXPIRY_EMAIL_SUBJECT,
  DEFAULT_POINTS_EXPIRY_EMAIL_BODY,
} from '@/constants/defaultPointsExpiryReminderTemplates.js';
import {
  DEFAULT_TIER_DOWNGRADE_LINE_BODY,
  DEFAULT_TIER_DOWNGRADE_SMS_BODY,
  DEFAULT_TIER_DOWNGRADE_EMAIL_SUBJECT,
  DEFAULT_TIER_DOWNGRADE_EMAIL_BODY,
} from '@/constants/defaultTierDowngradeReminderTemplates.js';

export default {
  name: 'NotificationChannelSelector',
  components: { LineMsgTemplatePicker },

  props: {
    /** 已選取的管道陣列，支援 v-model:channels */
    channels: {
      type: Array,
      default: () => []
    },
    /** 各管道對應的範本 ID，支援 v-model:templateIds */
    templateIds: {
      type: Object,
      default: () => ({})
    },
    /**
     * LINE 管道是否使用固定格式（不需選範本）
     * 批次發券確認步驟使用
     */
    lineFixed: {
      type: Boolean,
      default: false
    },
    /**
     * 自動發送（註冊禮／升等禮／生日禮）等：顯示未選範本時的系統預設純文字預覽
     */
    showDefaultTemplatePreview: {
      type: Boolean,
      default: false
    },
    /** auto_send：註冊禮等預設文案；points_expiry：點數到期提醒預設文案；tier_downgrade：降等前提醒預設文案 */
    templatePreviewPreset: {
      type: String,
      default: 'auto_send',
      validator: (v) => ['auto_send', 'points_expiry', 'tier_downgrade'].includes(v)
    }
  },

  emits: ['update:channels', 'update:templateIds'],

  setup(props, { emit }) {
    // LINE 使用完整 template 物件（由 LineMsgTemplatePicker 管理，selectedId prop 負責初始還原）
    const lineSelectedTemplate = ref(null);

    const PRESET_TEMPLATES = {
      points_expiry: {
        line: DEFAULT_POINTS_EXPIRY_LINE_BODY,
        sms: DEFAULT_POINTS_EXPIRY_SMS_BODY,
        emailSubject: DEFAULT_POINTS_EXPIRY_EMAIL_SUBJECT,
        emailBody: DEFAULT_POINTS_EXPIRY_EMAIL_BODY,
      },
      tier_downgrade: {
        line: DEFAULT_TIER_DOWNGRADE_LINE_BODY,
        sms: DEFAULT_TIER_DOWNGRADE_SMS_BODY,
        emailSubject: DEFAULT_TIER_DOWNGRADE_EMAIL_SUBJECT,
        emailBody: DEFAULT_TIER_DOWNGRADE_EMAIL_BODY,
      },
      auto_send: {
        line: DEFAULT_AUTO_SEND_LINE_BODY,
        sms: DEFAULT_AUTO_SEND_SMS_BODY,
        emailSubject: DEFAULT_AUTO_SEND_EMAIL_SUBJECT,
        emailBody: DEFAULT_AUTO_SEND_EMAIL_BODY,
      },
    };
    const presetTemplates = computed(
      () => PRESET_TEMPLATES[props.templatePreviewPreset] || PRESET_TEMPLATES.auto_send
    );

    const defaultLineBody = computed(() => presetTemplates.value.line);
    const defaultSmsBody = computed(() => presetTemplates.value.sms);
    const defaultEmailSubject = computed(() => presetTemplates.value.emailSubject);
    const defaultEmailBody = computed(() => presetTemplates.value.emailBody);

    const notifTemplates = ref([]);
    const emailTemplates = computed(() => notifTemplates.value.filter(t => t.channel === 'email' && t.status === 'active'));
    const smsTemplates   = computed(() => notifTemplates.value.filter(t => t.channel === 'sms'   && t.status === 'active'));

    const loadNotifTemplates = async () => {
      try {
        const res = await notificationTemplateService.list({ pageSize: 100, status: 'active' });
        notifTemplates.value = res.data?.items || [];
      } catch (err) {
        console.error('[NotificationChannelSelector] 載入通知範本失敗:', err);
      }
    };

    const toggle = (channel, checked) => {
      const next = [...props.channels];
      if (checked) {
        if (!next.includes(channel)) next.push(channel);
      } else {
        const idx = next.indexOf(channel);
        if (idx >= 0) next.splice(idx, 1);
        // 取消管道時清除對應範本
        const ids = { ...props.templateIds };
        delete ids[channel];
        emit('update:templateIds', ids);
        // 若取消 LINE，同步清除本地選取
        if (channel === 'line') lineSelectedTemplate.value = null;
      }
      emit('update:channels', next);
    };

    const setTemplate = (channel, templateId) => {
      const ids = { ...props.templateIds };
      if (templateId) {
        ids[channel] = templateId;
      } else {
        delete ids[channel];
      }
      emit('update:templateIds', ids);
    };

    const onLineTemplateSelect = (template) => {
      lineSelectedTemplate.value = template;
      setTemplate('line', template?.id || null);
    };

    onMounted(loadNotifTemplates);

    return {
      lineSelectedTemplate,
      emailTemplates,
      smsTemplates,
      toggle,
      setTemplate,
      onLineTemplateSelect,
      templatePreviewPreset: computed(() => props.templatePreviewPreset),
      defaultLineBody,
      defaultSmsBody,
      defaultEmailSubject,
      defaultEmailBody,
    };
  }
};
</script>

<style scoped>
.section-hint-block {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
}

.section-hint {
  font-size: 13px;
  color: #6b7280;
  margin: 0 0 8px;
}

.section-hint--emphasis {
  color: #374151;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 10px 12px;
  margin-top: 8px;
}

.section-hint--emphasis code {
  font-size: 12px;
  background: #fef3c7;
  padding: 1px 6px;
  border-radius: 4px;
}

.send-logic-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.send-logic-list li {
  font-size: 13px;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 6px;
}

.send-logic-list li i {
  font-size: 15px;
  flex-shrink: 0;
}

.channel-logic-hint {
  font-size: 11px;
  color: #9ca3af;
  background: #f3f4f6;
  border-radius: 4px;
  padding: 2px 7px;
  white-space: nowrap;
  flex-shrink: 0;
}

.channel-logic-hint--always {
  color: #3b82f6;
  background: #eff6ff;
}

.notification-channels {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.notification-channel-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.notification-channel-row.is-expanded {
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
}

.channel-row-header {
  display: flex;
  align-items: center;
  gap: 16px;
}

.channel-name {
  font-size: 14px;
  font-weight: 600;
  min-width: 52px;
  white-space: nowrap;
}

/* ===== 管道開關 ===== */
.ch-toggle-switch {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;
  flex-shrink: 0;
  cursor: pointer;
}

.ch-toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.ch-toggle-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #d1d5db;
  border-radius: 22px;
  transition: background 0.2s;
}

.ch-toggle-slider::before {
  content: '';
  position: absolute;
  width: 16px;
  height: 16px;
  left: 3px;
  top: 3px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.ch-toggle-switch input:checked + .ch-toggle-slider {
  background: #4caf50;
}

.ch-toggle-switch input:checked + .ch-toggle-slider::before {
  transform: translateX(18px);
}

.ch-toggle-status {
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.ch-toggle-status.on { color: #4caf50; }
.ch-toggle-status.off { color: #9ca3af; }

.template-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}

.channel-icon {
  font-size: 18px;
}

.channel-icon.line  { color: #06c755; }
.channel-icon.email { color: #3b82f6; }
.channel-icon.sms   { color: #f59e0b; }

.template-select {
  flex: 1;
  max-width: 400px;
}

.channel-fixed-hint {
  font-size: 13px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 4px;
}

.line-picker-wrapper {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 14px;
}

.picker-goto-link {
  margin-top: 8px;
  font-size: 12px;
}

.picker-goto-link a {
  color: #3b82f6;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.picker-goto-link a:hover {
  text-decoration: underline;
}

.no-template-hint {
  font-size: 12px;
  color: #9ca3af;
}

.no-template-hint a {
  color: #3b82f6;
  text-decoration: none;
  margin-left: 4px;
}

.manage-templates-link {
  margin-top: 12px;
  font-size: 13px;
}

.manage-templates-link a {
  color: #3b82f6;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.link-sep {
  margin: 0 8px;
  color: #d1d5db;
}

.default-template-preview {
  margin-top: 12px;
  padding: 10px 12px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
}

.default-template-preview--full {
  width: 100%;
  flex-basis: 100%;
}

.default-preview-title {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: #166534;
  display: flex;
  align-items: center;
  gap: 6px;
}

.default-preview-subject {
  margin: 0 0 8px;
  font-size: 13px;
  color: #1e293b;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
}

.default-preview-label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.default-preview-body {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: #334155;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  max-height: 200px;
  overflow-y: auto;
}
</style>
